import { defineStore } from "pinia";
import { ref } from "vue";
import { invalidateSifcopDetailCache, setCachedSifcopDetail } from "@/utils/sifcopDetailCache";

const mapDoc = (doc) => {
  const raw = doc?.acta || {};
  return {
    id: raw.ActaSifcopID || null,
    identificador: raw.Identificador || "",
    datosGenerales: {
      organismoInterviniente: raw.OrganismoInterviniente || "",
      dependenciaPolicial: raw.DependenciaPolicial || "",
      lugar: raw.Lugar || "",
      fecha: raw.Fecha || "",
      departamento: raw.Departamento || "",
      provincia: raw.Provincia || "",
      hora: raw.Hora || "",
      dia: raw.Dia || "",
      mes: raw.Mes || "",
      anio: raw.Anio || "",
      funcionarioPolicialSuscribe: raw.FuncionarioPolicialQueSuscribe || "",
      usuarioSifcop: raw.UsuarioSIFCOP || "",
      numeroRequerimientoJudicial: raw.NumeroRequerimientoJudicial || "",
      fechaCreacion: raw.FechaCreacion || "",
    },
    persona: {
      nombreApellido: raw.NombreApellido || "",
      documento: raw.Documento || "",
      fechaNacimiento: raw.FechaNacimiento || "",
      nacionalidad: raw.Nacionalidad || "",
      domicilio: raw.Domicilio || "",
    },
    vehiculos: (doc?.vehiculos || []).map((item) => ({
      dominio: item.Dominio || "",
      anio: item.Anio || "",
      tipoVehiculo: item.TipoVehiculo || "",
      marca: item.Marca || "",
      numeroMotor: item.NumeroMotor || "",
      numeroChasis: item.NumeroChasis || "",
      modelo: item.Modelo || "",
      color: item.Color || "",
    })),
    armas: (doc?.armas || []).map((item) => ({
      tipoArma: item.TipoArma || "",
      marca: item.Marca || "",
      modelo: item.Modelo || "",
      calibre: item.Calibre || "",
      numeroSerie: item.NumeroSerie || "",
    })),
    elementos: (doc?.elementos || []).map((item) => ({
      tipoElemento: item.TipoElemento || "",
      identificacion: item.Identificacion || "",
      descripcion: item.Descripcion || "",
      modelo: item.Modelo || "",
    })),
  };
};

export const useSifcopDetailStore = defineStore("sifcopDetail", () => {
  const acta = ref(null);
  const rawDoc = ref(null);
  const isLoading = ref(false);
  const fetchError = ref(null);

  const fetchById = async (id) => {
    if (!id) return;

    isLoading.value = true;
    fetchError.value = null;

    try {
      const { useRest } = await import("@/composables/useRest");
      const rest = useRest();
      const SIFCOP_BASE = process.env.VUE_APP_BACKEND_ENVIRONMENT.replace("/api/v1", "/api/v3");
      const response = await rest.get({ url: `${SIFCOP_BASE}/sifcop/${id}` });
      const doc = response?.data?.doc || response?.doc;

      if (doc) {
        setCachedSifcopDetail(id, doc);
        rawDoc.value = doc;
        acta.value = mapDoc(doc);
      } else {
        fetchError.value = "No se encontró el formulario SIFCOP.";
      }
    } catch (err) {
      fetchError.value = err?.message || "Error al cargar el formulario SIFCOP.";
    } finally {
      isLoading.value = false;
    }
  };

  const deleteById = async (id) => {
    if (!id) return false;

    isLoading.value = true;
    try {
      const { useRest } = await import("@/composables/useRest");
      const rest = useRest();
      const SIFCOP_BASE = process.env.VUE_APP_BACKEND_ENVIRONMENT.replace("/api/v1", "/api/v3");
      const response = await rest.del({ url: `${SIFCOP_BASE}/sifcop/${id}` });
      const hasError = response?.data?.error || response?.error;
      if (hasError) return false;
      invalidateSifcopDetailCache(id);
      acta.value = null;
      return true;
    } catch {
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const clear = () => {
    acta.value = null;
    rawDoc.value = null;
    fetchError.value = null;
  };

  return {
    acta,
    rawDoc,
    isLoading,
    fetchError,
    fetchById,
    deleteById,
    clear,
  };
});
